import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Landmark, ChevronDown, Search, Check } from 'lucide-react'
import { hapticSelectionChanged } from '../../../utils/haptic'

const normalize = (value) => String(value || '').toLowerCase().replace(/ё/g, 'е').trim()

/**
 * SbpBankSelect — выпадающий список банков СБП с поиском по названию.
 * Крупные банки (popular) идут первыми в порядке API и отделены чертой от остальных.
 */
const SbpBankSelect = ({ id, banks, value, onChange, disabled }) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const rootRef = useRef(null)
  const searchRef = useRef(null)

  const selected = banks.find((bank) => bank.id === value)

  const filtered = useMemo(() => {
    const needle = normalize(query)
    return needle ? banks.filter((bank) => normalize(bank.name).includes(needle)) : banks
  }, [banks, query])

  // Закрытие по клику вне списка и по Escape
  useEffect(() => {
    if (!isOpen) return undefined
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setIsOpen(false)
    }
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    searchRef.current?.focus()
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [isOpen])

  const select = (bank) => {
    hapticSelectionChanged()
    onChange(bank.id)
    setIsOpen(false)
    setQuery('')
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        id={id}
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className="w-full flex items-center gap-2 bg-dark-primary border border-white border-opacity-10 rounded-lg ps-3 pe-3 py-3 text-start focus:border-accent-primary focus:outline-none disabled:opacity-50"
      >
        <Landmark size={18} className="flex-shrink-0 text-accent-primary" />
        <span className={`flex-1 truncate ${selected ? 'text-white' : 'text-text-secondary'}`}>
          {selected ? selected.name : t('rubWithdraw.bankPlaceholder')}
        </span>
        <ChevronDown size={18} className={`flex-shrink-0 text-text-secondary transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-30 mt-2 w-full rounded-lg border border-white/10 bg-surface shadow-xl">
          <div className="relative border-b border-white/5 p-2">
            <Search size={16} className="absolute start-4 top-1/2 -translate-y-1/2 text-text-secondary" />
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('rubWithdraw.bankSearch')}
              autoComplete="off"
              className="w-full bg-dark-primary border border-white border-opacity-10 rounded-md ps-8 pe-3 py-2 text-sm text-white focus:border-accent-primary focus:outline-none"
            />
          </div>
          <ul role="listbox" className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <li className="px-3 py-3 text-sm text-text-secondary">{t('rubWithdraw.bankNotFound')}</li>
            )}
            {filtered.map((bank, index) => {
              const isSelected = bank.id === value
              // Черта между крупными банками и остальными
              const isFirstRegular = !bank.popular && index > 0 && filtered[index - 1].popular
              return (
                <li key={bank.id} role="option" aria-selected={isSelected} className={isFirstRegular ? 'border-t border-white/10 mt-1 pt-1' : ''}>
                  <button
                    type="button"
                    onClick={() => select(bank)}
                    className={`w-full flex items-center gap-2 px-3 py-2.5 text-start text-sm transition-colors hover:bg-white/5 ${
                      isSelected ? 'text-accent-primary' : bank.popular ? 'text-white font-medium' : 'text-white'
                    }`}
                  >
                    <span className="flex-1 truncate">{bank.name}</span>
                    {isSelected && <Check size={16} className="flex-shrink-0" />}
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}

export default SbpBankSelect
